import { TOC_STATUS } from 'core/chatbot/types/courseToc.constants';
import type { GetUserCourseTocWithProgressResponse, TocGlobalProgress, TocKcItem, TocLessonItem, TocUnitItem } from 'core/chatbot/types/courseToc';

/**
 * Hardens the raw `GET /courses/:courseId/toc` body before it reaches the tree.
 * The service returns `response.json()` verbatim, so a partial/malformed 200
 * (missing `units`, a unit without `lessons`/`kcs`, a KC without `currentKnowledge`)
 * would otherwise crash `data.units.length` / `unit.lessons.map` or render `NaN%`.
 * Every list is coerced to an array and `currentKnowledge` to a finite number.
 */

function toArray<T>(value: readonly T[] | undefined | null): T[] {
  return Array.isArray(value) ? [...value] : [];
}

function normalizeKc(kc: Partial<TocKcItem> | undefined | null): TocKcItem {
  return {
    kcId: kc?.kcId ?? '',
    statement: kc?.statement ?? '',
    currentKnowledge: Number.isFinite(kc?.currentKnowledge) ? (kc?.currentKnowledge as number) : 0,
    status: kc?.status ?? TOC_STATUS.NOT_STARTED,
  };
}

function normalizeLesson(lesson: TocLessonItem): TocLessonItem {
  return { ...lesson, kcs: toArray(lesson?.kcs).map(normalizeKc) };
}

function normalizeUnit(unit: TocUnitItem): TocUnitItem {
  return {
    ...unit,
    kcs: toArray(unit?.kcs).map(normalizeKc),
    lessons: toArray(unit?.lessons).map(normalizeLesson),
  };
}

/** Server-computed course-wide figure; coerce a missing/malformed value to `null` (no data). */
function normalizeGlobalProgress(globalProgress: Partial<TocGlobalProgress> | undefined | null): TocGlobalProgress | null {
  if (!globalProgress) return null;
  const { masteredKcCount, kcCount } = globalProgress;
  if (!Number.isFinite(masteredKcCount) || !Number.isFinite(kcCount)) return null;
  return { masteredKcCount: masteredKcCount as number, kcCount: kcCount as number };
}

function normalizeCourseToc(raw: GetUserCourseTocWithProgressResponse | null | undefined, courseId: string): GetUserCourseTocWithProgressResponse {
  return {
    courseId: raw?.courseId ?? courseId,
    title: raw?.title ?? '',
    units: toArray(raw?.units).map(normalizeUnit),
    globalProgress: normalizeGlobalProgress(raw?.globalProgress),
  };
}

export default normalizeCourseToc;
